import type { DataTableColumnDef } from 'components/tables/data-table';
import { getFilterOptionsFromClassifier } from 'components/tables/list-table/filterUtil';
import { FilterConfig, MainFilter } from 'components/tables/list-table/ListTableTypes';
import { Fragment } from 'react';
import { useLazyGetMinGroupsListWithFiltersQuery } from 'store/api/groups';
import { ClassifierData } from 'types/classifiers';
import { PartyRevenueDetails, PartyRevenueFigures, PartyRevenueTotals } from 'types/reporting/executive';
import { t } from 'utils/getTranslations';
import {
    ReportContactLink,
    ReportEmployeeLink,
    ReportNumberCell,
    TONED_COLUMN_META,
    tonedCell,
    GREEN_OR_RED,
} from '../reportCells';
import { inBaseCurrency } from '../reportCurrency';
import {
    DATE_BASIS_SELECTOR,
    employeeFilter,
    periodFilter,
    periodQuickFilter,
    quickFiltersFrom,
    singleChoiceFilter,
} from '../reportFilters';

/**
 * The filter bar and the columns the Top clients and Top payers by revenue reports share. The two differ only in
 * the party they rank, so each page hands in its party's filter, search selectors and columns.
 */

export const PARTY_REVENUE_CLASSIFIERS = ['COUNTRY', 'BUSINESS_AREA_TYPE'];

/** Which date the period bounds: the invoice date (the report's default) or the date the service was rendered. */
function revenueDateBasisFilter(): MainFilter {
    return singleChoiceFilter(DATE_BASIS_SELECTOR, 'reporting.filter.date-basis', [
        { label: t('reporting.filter.date-basis-invoice-date'), value: 'INVOICE_DATE' },
        { label: t('reporting.filter.date-basis-service-date'), value: 'SERVICE_DATE' },
    ]);
}

function contactGroupFilter(selector: string, labelId: string): MainFilter {
    return {
        type: 'dynamicmultiselect',
        label: t(labelId),
        selector,
        paginated: {
            fetch: useLazyGetMinGroupsListWithFiltersQuery,
            searchField: 'groupName',
            getOptionIdentifier: 'id',
            getOptionLabel: 'groupName',
        },
    } as MainFilter;
}

function classifierFilter(selector: string, labelId: string, classifier: ClassifierData | undefined): MainFilter {
    return {
        type: 'multiselect',
        label: t(labelId),
        selector,
        options: getFilterOptionsFromClassifier(classifier),
    };
}

export function buildPartyRevenueFilterConfig(
    partyFilter: MainFilter,
    searchSelectors: string[],
    classifiers: Record<string, ClassifierData>
): FilterConfig {
    const filters: MainFilter[] = [
        revenueDateBasisFilter(),
        periodFilter(),
        partyFilter,
        employeeFilter('relationshipPartnerId', 'reporting.filter.relationship-partner'),
        classifierFilter('countryCode', 'reporting.filter.country', classifiers.COUNTRY),
        classifierFilter('businessAreaCode', 'reporting.filter.business-area', classifiers.BUSINESS_AREA_TYPE),
        contactGroupFilter('contactGroupId', 'reporting.filter.contact-group'),
        contactGroupFilter('excludedContactGroupId', 'reporting.filter.excluded-contact-group'),
    ];
    return {
        hideFilterDividers: true,
        quickFilterChips: true,
        periodQuickFilter: periodQuickFilter({ defaultPeriod: 'current-year' }),
        quickFilters: quickFiltersFrom(filters, { dateBasis: 'reporting.filter.date-basis' }),
        searchBarSelectors: searchSelectors.map((selector) => ({ selector, comparator: '=lk=' })),
        filters,
    };
}

type PartyRevenueRow = PartyRevenueFigures & PartyRevenueDetails;

/** The ranked party's number and name columns: their sort keys, labels and values. */
export type PartyColumns<Row> = {
    numberId: string;
    numberLabelId: string;
    nameId: string;
    nameLabelId: string;
    partyId: (row: Row) => number;
    partyNumber: (row: Row) => string | null;
    partyName: (row: Row) => string;
};

type FigureKey = Exclude<keyof PartyRevenueFigures, 'baseCurrency'>;

/** The figure columns; an amount is in the base currency, which its header names. */
const FIGURE_COLUMNS: { key: FigureKey; labelId: string; minPx: number; isAmount?: boolean }[] = [
    { key: 'invoicedHours', labelId: 'reporting.column.invoiced-hours', minPx: 130 },
    { key: 'invoicedAmount', labelId: 'reporting.column.invoiced-amount', minPx: 150, isAmount: true },
    { key: 'expensesAmount', labelId: 'reporting.column.expenses-amount', minPx: 140, isAmount: true },
    { key: 'otherAmount', labelId: 'reporting.column.other-amount', minPx: 130, isAmount: true },
    { key: 'totalAmount', labelId: 'reporting.column.total-amount', minPx: 150, isAmount: true },
];

function textColumn<Row extends PartyRevenueRow>(
    id: Exclude<keyof PartyRevenueDetails, 'relationshipPartnerEmployees'>,
    labelId: string
): DataTableColumnDef<Row, PartyRevenueTotals> {
    return {
        id,
        accessorFn: (row) => row[id],
        header: t(labelId),
        meta: { widthWeight: 1, minPx: 140 },
    };
}

export function buildPartyRevenueColumns<Row extends PartyRevenueRow>(
    rows: Row[] | undefined,
    party: PartyColumns<Row>,
    currency: string | null
): DataTableColumnDef<Row, PartyRevenueTotals>[] {
    const figureColumns = FIGURE_COLUMNS.map(
        ({ key, labelId, minPx, isAmount }): DataTableColumnDef<Row, PartyRevenueTotals> => {
            return {
                id: key,
                accessorFn: (row) => row[key],
                header: isAmount ? inBaseCurrency(t(labelId), currency) : t(labelId),
                meta: {
                    ...TONED_COLUMN_META,
                    widthWeight: 0.8,
                    minPx,
                    textAlign: 'right',
                    nullHandling: 'nullsLast',
                    renderTotal: (totals) => <ReportNumberCell value={totals?.[key]} />,
                },
                cell: tonedCell(rows, (row) => row[key], GREEN_OR_RED),
            };
        }
    );
    return [
        {
            id: party.numberId,
            accessorFn: party.partyNumber,
            header: t(party.numberLabelId),
            meta: { widthWeight: 0.5, minPx: 90 },
            cell: ({ row }) => (
                <ReportContactLink partyId={party.partyId(row.original)}>
                    {party.partyNumber(row.original)}
                </ReportContactLink>
            ),
        },
        {
            id: party.nameId,
            accessorFn: party.partyName,
            header: t(party.nameLabelId),
            meta: { widthWeight: 1.3, minPx: 180 },
            cell: ({ row }) => (
                <ReportContactLink partyId={party.partyId(row.original)}>
                    {party.partyName(row.original)}
                </ReportContactLink>
            ),
        },
        textColumn<Row>('country', 'reporting.column.country'),
        textColumn<Row>('businessAreas', 'reporting.column.business-areas'),
        {
            ...textColumn<Row>('relationshipPartners', 'reporting.column.relationship-partners'),
            cell: ({ row }) =>
                row.original.relationshipPartnerEmployees.map((partner, index) => (
                    <Fragment key={partner.employeeId}>
                        {index > 0 && ', '}
                        <ReportEmployeeLink employeeId={partner.employeeId}>{partner.name}</ReportEmployeeLink>
                    </Fragment>
                )),
        },
        ...figureColumns,
    ];
}
